import { IEntityDashboardData } from 'app/shared/interfaces/entityDashboard';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { fuseAnimations } from '@fuse/animations';
import { BaseComponent } from '../base/base.component';
import { ReplaySubject, Subscription } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { SubjectTypeService } from 'app/shared/services/subject-type.service';
import { EntityDashboardService } from 'app/shared/services/entity-dashboard.service';
import { EventService } from 'app/shared/services/event.service';
import { LayoutBuilderComponent } from 'app/components/layout-builder/layout-builder.component';
import { UserSecurityService } from 'app/shared/services/user-security.service';
import { KeyValuePair } from 'app/shared/models/keyvaluepair';
import { IEntityWithLinks } from 'app/shared/interfaces/entity';
import { Widget, WidgetGroup } from 'app/shared/models/widget';
import { SubjectType } from 'app/shared/models/subject-type';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from 'app/shared/services/auth.service';
import { enumReviewStatus } from 'app/shared/models/review';
import { SubjectLinkService } from 'app/shared/services/subject-link.service';
import { LinkWizardComponent } from './link-wizard/link-wizard.component';
import { DefaultDataService } from 'app/shared/services/default-data.service';
import { ReviewDialogComponent } from '../referrals/review-dialog/review-dialog.component';
import { AssessmentDialogComponent } from './assessment-dialog/assessment-dialog.component';
import { Assessment } from 'app/shared/models/assessment';
import { Subject } from 'app/shared/models/subject';
import { PrintSubjectDialogComponent } from '../subjects/print-subject-dialog/print-subject-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import {
  MessageBoxComponent,
  MessageBoxParameters,
  MessageBoxType,
} from 'app/components/messageBox/messageBox.component';
import { plural } from 'pluralize';
import { SubjectSettingService } from 'app/shared/services/subject-setting.service';
import { SubjectSetting } from 'app/shared/models/subject-setting';
import { DateFormatPipe } from 'app/pipes/date-format.pipe';
import { UserService } from 'app/shared/services/user.service';
import { environment } from 'environments/environment';
import { User } from 'app/shared/models/user';
import { SubjectService } from 'app/shared/services/subject.service';
import { HyperLawService } from 'app/shared/services/hyper-law.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { RetentionPromptComponent } from '../subjects/subject-edit/retention-prompt/retention-prompt.component';
import { messageService } from 'app/shared/services/messages/message.module';

@AutoUnsubscribe()
@Component({
  selector: 'entity-dashboard',
  templateUrl: './entity-dashboard.component.html',
  styleUrls: ['./entity-dashboard.component.scss'],
  animations: fuseAnimations,
})
export class EntityDashboardComponent
  extends BaseComponent
  implements AfterViewInit, OnDestroy
{
  _data!: IEntityDashboardData;
  links: SubjectType[] = [];
  hasChanges = false;
  readOnly = false;
  userReadOnly = false;
  isAdmin = false;
  addEventRaised = false;
  entityWithLinks!: IEntityWithLinks;
  availableWidgets!: Widget[];
  fixedWidgets!: Widget[];
  previousIds: KeyValuePair[] = [];
  userList: string[] = [];
  userListString: string = '';

  includeClosedCasesInReviewFlag = false;

  isPrimarySubject = false;
  reviewStatus = enumReviewStatus.None;
  accepted = false;
  rejected = false;
  pending = true;
  canAccept = false;
  canReject = false;
  canReview = false;
  canConvertToSubject = false;
  allowAssessment = false;
  hasSubjectMarkers = false;
  hasLinkedSubjectMarkers = false;
  incompleteAssessments: Assessment[] = [];
  subjectTypeName = '';
  saveLayoutRequested = false;
  showSecurity = true;
  showCasePrepUI = false;
  casePrepUIDisabled = false;
  deadlineDaysLeft: number = 0;
  deadlineText: string = '';

  @Input() set data(val: IEntityDashboardData) {
    if (val) {
      const differentEntity = !(
        this._data &&
        this._data.entity &&
        val &&
        val.entity &&
        this._data.entity.uid === val.entity.uid
      );

      // patch linkedSubjectMarkers
      if (val.entity) {
        val.entity.linkedSubjectMarkers =
          this.entityDashboardService.getLinkedSubjectMarkers(val.entity);
      }

      this.subjectTypeName = val.isReferral ? 'Referral' : val.subjectType.name;

      this._data = val;
      if (this._data && this._data.entity && this._data.entity.id) {
        this.addToHistory();

        this.includeClosedCasesInReviewFlag =
          this.defaultDataService.includeClosedCasesInReviews;
        this.isPrimarySubject = this._data.primary;
        this.subjectTypeName =
          this._data.entity.isReferral && this._data.entity.referral
            ? 'Referral'
            : this._data.subjectType.name;
        this.hasSubjectMarkers =
          this._data.entity.subjectMarkers &&
          this._data.entity.subjectMarkers.length > 0;
        this.hasLinkedSubjectMarkers =
          this._data.entity.linkedSubjectMarkers &&
          this._data.entity.linkedSubjectMarkers.length > 0;
        this.readOnly =
          this._data.entity &&
          (this._data.entity.archived || this.userReadOnly);

        // Get deadlineDaysLeft from entity

        var s = new Subject(this._data.entity);
        this.deadlineDaysLeft = s.getDeadlineDaysLeft();
        this.deadlineText = Subject.getDeadlineText(this.deadlineDaysLeft);

        this.reviewStatus =
          this._data.entity.isReferral && this._data.entity.referral
            ? this._data.entity.reviewStatus
            : enumReviewStatus.None;
        this.accepted = this.reviewStatus === enumReviewStatus.Accepted;
        this.rejected = this.reviewStatus === enumReviewStatus.Rejected;
        this.pending =
          this.reviewStatus === enumReviewStatus.Pending ||
          this.reviewStatus === enumReviewStatus.Reviewed;

        const allowReview =
          this.defaultDataService.enableReferralsAndAssessments &&
          this.isPrimarySubject &&
          !this._data.entity.archived;
        // accept only allowed for referrals not yet accepted, or subjects
        const allowAccept =
          !this._data.entity.isReferral ||
          (this._data.entity.isReferral &&
            this._data.entity.reviewStatus !== enumReviewStatus.Accepted);
        this.canAccept = allowReview && allowAccept;
        this.canReject = allowReview && allowAccept;
        this.canReview = allowReview;
        this.allowAssessment =
          allowReview &&
          allowAccept &&
          this.defaultDataService.enableAssessments;
        if (this._data.entity.subjectAssessments) {
          this.incompleteAssessments = this._data.entity.subjectAssessments
            .filter((i) => !i.assessment.completed)
            .map((i) => i.assessment);
        }

        this.canConvertToSubject =
          this._data.entity.isReferral &&
          this._data.entity.reviewStatus === enumReviewStatus.Accepted;

        this.hasChanges = false;

        // if different, notify viewed
        if (differentEntity) {
          this.clearLayout();
          if (!this._data.entity.isReferral) {
            this.eventService.viewSubject(this._data.entity);
          }
        }

        this.userListString = '';
        if (
          this._data.entity.restricted === true &&
          environment.limitAccessToSubjectsByOrg == true
        ) {
          this._data.service
            .getUserSecurity(this._data.entity)
            .pipe(take(1))
            .subscribe((selectedUsers) => {              
              var cnt = 0;
              selectedUsers.forEach((element) => {
                var u = this.userManager.getByIdFromCache(element);
                if (u?.isAdmin !== true) {
                  cnt = cnt + 1;
                  if (cnt > 1) this.userListString = this.userListString + ', ';
                  this.userListString =
                    this.userListString + u?.firstName + ' ' + u?.lastName;
                }
              });

              var idx = this.userListString.lastIndexOf(',');
              if (idx > -1) {
                this.userListString =
                  this.userListString.substring(0, idx) +
                  ' and' +
                  this.userListString.substring(idx + 1);
              }

              if (this.userListString.length > 0)
                this.userListString = ' to ' + this.userListString;

              // For Testing layout
              // this._data.entity.archived=true;
              // this.accepted=true;
              // this.hasSubjectMarkers=true;
            });
        }

        this.updateSecurity();

        // update to reflect any changes
        this.refresh(this._data.entity, true);

        // check review status
        //We set the flag, if we set the Flag to true we are to include the Closed Cases in reviews
        //If not, we still want to open the dialogue option to open for cases which are not closed

        if (
          this.includeClosedCasesInReviewFlag == true &&
          this._data.entity.stateId >= 1
        ) {
          this.checkAndPromptReviewStatus(this._data);
        }

        if (
          this.includeClosedCasesInReviewFlag == false &&
          this._data.entity.stateId > 1
        ) {
          this.checkAndPromptReviewStatus(this._data);
        }

        this.showCasePrepUI = this.hyperLawService.isConfigValid();
        if (this.showCasePrepUI && this.subject) {
          this.hyperLawService.getCaseAndUpdateSubject(this.subject);
        }
      }
    }
  }

  @Output() onGoBack: EventEmitter<any> = new EventEmitter();
  @Output() onAdd: EventEmitter<any> = new EventEmitter();
  @Output() onRefreshData: EventEmitter<any> = new EventEmitter();

  onParamsChanged: Subscription;
  onEntityChanged: Subscription;
  onLayoutChanged: Subscription;
  onCurrentUserChanged: Subscription;
  onSaveEntityLayout: Subscription;
  onSecurityChanged: Subscription;
  onSubjectTypesChanged: Subscription;
  onEntityMarkedAsDirty: Subscription;
  onHyperLawConfigValidChanged: Subscription;
  onHyperLawDisableUIChanged: Subscription;
  onHyperLawCaseAdded: Subscription;
  saveEntityLayout: ReplaySubject<string> = new ReplaySubject<string>(1, 100);

  @ViewChild('layout') layoutBuilder!: LayoutBuilderComponent;

  constructor(
    private activeRoute: ActivatedRoute,
    private authService: AuthService,
    private subjectService: SubjectService,
    private entityDashboardService: EntityDashboardService,
    private eventService: EventService,
    private subjectTypeService: SubjectTypeService,
    private subjectLinkService: SubjectLinkService,
    private userSecurityService: UserSecurityService,
    private defaultDataService: DefaultDataService,
    private subjectSettingService: SubjectSettingService,
    private userManager: UserService,
    private hyperLawService: HyperLawService,
    private router: Router,
    public dialog: MatDialog
  ) {
    super();

    this.onParamsChanged = this.activeRoute.params.subscribe((_routeParams) => {
      this.saveOutstandingChanges();
    });

    // subscribe to saveEntity
    this.onSaveEntityLayout = this.saveEntityLayout
      .pipe(
        debounceTime(1000) // 1 second debounce
      )
      .subscribe((message) => {
        this.saveLayoutRequested = false;
        this._data.service
          .saveLayout(this._data.entity, message)
          .pipe(take(1))
          .subscribe();
      });

    // setup subscription for getting the possible subject types for linking
    this.onSubjectTypesChanged = this.subjectTypeService.onChanged.subscribe(
      (response) => {
        this.links = response.filter((i) => !i.hideRecord);
        this.links.sort((a, b) => a.name.localeCompare(b.name));
      }
    );

    this.onSecurityChanged =
      this.userSecurityService.onSecurityChanged.subscribe((response) => {
        if (response) {
          this.onRefreshData.next(
            response.currentUserAllowedAccess ? response.entity.uid : undefined
          );
        }
      });

    // if entity changed, process changes
    this.onEntityChanged =
      this.entityDashboardService.onEntityChanged.subscribe((response) => {
        if (response) {
          this.entityChanged(response.entity, response.message);
        }
      });

    this.onEntityMarkedAsDirty =
      this.eventService.onSubjectMarkedAsDirty.subscribe((response) => {
        if (
          response &&
          this._data &&
          this._data.entity &&
          this._data.entity.id === response.subjectId
        ) {
          this.entityChanged(this._data.entity, response.message);
        }
      });

    // if layout changed, save
    this.onLayoutChanged =
      this.entityDashboardService.onLayoutChanged.subscribe((entity) => {
        // this._data.entity.archived = this._data.entity.archived;
        this.saveLayoutRequested = true;
        this.saveEntityLayout.next(`Dashboard layout saved`);
        this.hasChanges = false;
      });

    this.onCurrentUserChanged = this.userManager.onCurrentUserChanged.subscribe(
      (user) => {
        this.updateSecurity(user);
      }
    );

    this.onHyperLawConfigValidChanged =
      this.hyperLawService.configValid.subscribe((val: boolean) => {
        this.showCasePrepUI = val;
        if (this.showCasePrepUI && this.subject) {
          this.hyperLawService.getCaseAndUpdateSubject(this.subject);
        }
      });

    this.onHyperLawDisableUIChanged = this.hyperLawService.disableUI.subscribe(
      (val: boolean) => {
        this.casePrepUIDisabled = val;
      }
    );

    this.onHyperLawCaseAdded =
      this.hyperLawService.caseAddedForSubjectUid.subscribe((uid: string) => {
        if (this.subject && this.subject.uid == uid) {
          this.subject.hasCasePreparationFile = true;
        }
      });

    // set properties
    this.userReadOnly = this.authService.isUserReadOnly();
    this.isAdmin = this.authService.hasPriv('admin,orgmanager');
  }

  get subject() {
    return this._data.entity as Subject;
  }

  ngAfterViewInit() {
    // setup grid options
    this.layoutBuilder.options = {
      fixedColWidth: this.entityDashboardService.gridSize,
      fixedRowHeight: this.entityDashboardService.gridSize,
      margin: this.entityDashboardService.gridMargin,
    };
  }

  ngOnDestroy() {
    this.saveOutstandingChanges();

    // unsubscribe listeners
    if (this.onParamsChanged) {
      this.onParamsChanged.unsubscribe();
    }
    if (this.onEntityChanged) {
      this.onEntityChanged.unsubscribe();
    }
    if (this.onLayoutChanged) {
      this.onLayoutChanged.unsubscribe();
    }
    if (this.onSaveEntityLayout) {
      this.onSaveEntityLayout.unsubscribe();
    }
    if (this.onSecurityChanged) {
      this.onSecurityChanged.unsubscribe();
    }
    if (this.onEntityMarkedAsDirty) {
      this.onEntityMarkedAsDirty.unsubscribe();
    }
    if (this.onSubjectTypesChanged) {
      this.onSubjectTypesChanged.unsubscribe();
    }
    if (this.onCurrentUserChanged) {
      this.onCurrentUserChanged.unsubscribe();
    }
    if (this.onHyperLawConfigValidChanged) {
      this.onHyperLawConfigValidChanged.unsubscribe();
    }
    if (this.onHyperLawDisableUIChanged) {
      this.onHyperLawDisableUIChanged.unsubscribe();
    }
    if (this.onHyperLawCaseAdded) {
      this.onHyperLawCaseAdded.unsubscribe();
    }
  }

  saveOutstandingChanges() {
    // make sure any outstanding layout changes are persisted to data store
    if (this.hasChanges) {
      this.saveLayout();
    }
    if (this.saveLayoutRequested) {
      this._data.service
        .saveLayout(this._data.entity, '')
        .pipe(take(1))
        .subscribe();
    }
  }

  addToHistory() {
    // stash the subject in our local history list if not already added
    const history = this.previousIds.length;
    if (
      history === 0 ||
      this.previousIds[history - 1].key !== this._data.entity.uid
    ) {
      const kvp = new KeyValuePair(
        this._data.entity.uid,
        this._data.subjectType.name
      );
      this.previousIds.push(kvp);
    }
  }

  add() {
    if (!this.addEventRaised) {
      this.onAdd.next(this._data.subjectType);
      this.addEventRaised = true;
    }
  }

  saveDashboard() {
    this.layoutBuilder.locked = true;
    if (this.hasChanges) {
      this.saveLayout();
    }
  }

  editDashboard() {
    this.layoutBuilder.locked = false;
  }

  layoutChanged(layout) {
    this.hasChanges = true;
  }

  saveChanges(layout) {
    if (this.hasChanges) {
      this.saveLayout();
    }
  }

  // set layout based on subject template, or subject type dashboard template
  setLayout(entity: IEntityWithLinks) {
    if (entity) {
      this.entityDashboardService
        .getLayoutWidgets(entity)
        .pipe(take(1))
        .subscribe((widgets) => {
          if (widgets) {
            this.layoutBuilder.layout = [...widgets];
          } else {
            this.entityDashboardService
              .getDefaultLayoutWidgets(entity)
              .pipe(take(1))
              .subscribe((w) => {
                this.layoutBuilder.layout = [...w];
              });
          }
        });
    }
  }

  clearLayout() {
    if (this.layoutBuilder) {
      this.layoutBuilder.layout = [];
      this.entityDashboardService.currentLayout = '';
    }
  }

  // get the available subject links and templates for the specified subject, and set the dashboard layout and list of available widgets for the dashboard accordingly
  refresh(entity: IEntityWithLinks, updateLayout: boolean = false) {
    if (entity && this.layoutBuilder) {
      if (updateLayout === true) {
        this.setLayout(entity);
      }
      this.getAvailableWidgets(entity);
      this.fixedWidgets = this.entityDashboardService.getFixedWidgets(entity);
    }
  }

  // build a list of widgets available to be displayed on the dashboard - this is used to populate the widget menu
  getAvailableWidgets(entity: IEntityWithLinks) {
    this.entityWithLinks = entity;
    if (this.layoutBuilder) {
      this.entityDashboardService
        .getAvailableWidgets(entity, this.layoutBuilder.layout)
        .pipe(take(1))
        .subscribe((widgets) => {
          this.availableWidgets = widgets;
        });
    }
  }

  addWidget(widget) {
    if (this.entityWithLinks) {
      this.getAvailableWidgets(this.entityWithLinks);
    }
  }

  removeWidget(widget) {
    if (this.entityWithLinks) {
      this.getAvailableWidgets(this.entityWithLinks);
    }
  }

  addLink(subjectType: SubjectType) {
    this.dialog
      .open(LinkWizardComponent, {
        data: {
          subject: new Subject(this._data.entity),
          subjectType,
          parentSubjectType: this._data.entity.subjectType,
        },
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result && result.data) {
          this._data.entity = result.data.subject;
          const subjectLink = result.data.subjectLink;
          const addToDashboard = result.data.addToDashboard;
          if (addToDashboard && subjectLink.linkedSubject) {
            this.subjectLinkService
              .hydrate(subjectLink)
              .pipe(take(1))
              .subscribe(() => {
                const linkedSubject = new Subject(subjectLink.linkedSubject);
                this.subjectService
                  .hydrate(linkedSubject)
                  .pipe(take(1))
                  .subscribe(() => {
                    this.entityDashboardService
                      .getLinkedSubjectCardWidget(
                        linkedSubject,
                        linkedSubject.summary,
                        subjectLink.relationship.name
                      )
                      .pipe(take(1))
                      .subscribe((widget) => {
                        this.layoutBuilder.addWidget(new WidgetGroup(widget));
                        this.saveLayout();
                      });
                  });
              });
          }
        }
      });
  }

  createCasePreparationFileClicked() {
    this.hyperLawService.addCase(this.subject);
  }

  openCasePreparationFileClicked() {
    this.hyperLawService.openCase(this.subject);
  }

  deleteCasePreparationFileClicked() {
    this.dialog
      .open(MessageBoxComponent, {
        data: new MessageBoxParameters(
          `Are you sure you want to delete the Case Preparation File for this ${this.subjectTypeName}?`,
          'Confirm',
          MessageBoxType.Confirm,
          true
        ),
        disableClose: false,
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'confirm') {
          this.hyperLawService.deleteCase(this.subject);
        }
      });
  }

  goBack() {
    if (this.previousIds.length > 1) {
      // pop the current entity off the list
      this.previousIds.pop();
      // ...and then pop the previous entity off the list
      this.onGoBack.next(this.previousIds.pop());
    }
  }

  print() {
    if (this._data && this._data.entity && this._data.subjectType) {
      this.dialog.open(PrintSubjectDialogComponent, {
        data: {
          subject: this._data.entity as Subject,
          subjectType: this._data.subjectType,
        },
      });
    }
  }

  saveLayout() {
    if (this._data.entity) {
      this.entityDashboardService.saveLayout(
        this._data.entity,
        this.layoutBuilder.layout
      );
    }
  }

  applySecurity() {
    this._data.service
      .getUserSecurity(this._data.entity)
      .pipe(take(1))
      .subscribe((selectedUsers: number[]) => {
        this.userSecurityService.apply(this._data.entity, selectedUsers);
      });
  }

  convertToSubject() {
    this._data.entity.isReferral = false;
    this.subjectService
      .saveAsObservable(this._data.entity)
      .pipe(take(1))
      .subscribe((savedSubject) => {
        this.router.navigateByUrl(
          `/subjects/${savedSubject.subjectType.name}/${savedSubject.uid}`
        );
      });
  }

  archive() {
    this.entityDashboardService.setArchiveFlag(this._data, true);
    this.saveDashboard();
  }

  changeRetentionPeriod() {
    this.dialog
      .open(RetentionPromptComponent, {
        data: this._data.entity,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result && result.data.retentionDate) {
          this.subjectService.save(
            result.data,
            'Retention Period Changed Successfully'
          );
        } else {
          messageService.error(
            `Subject not saved as required retention date not set.`
          );
        }
      });
  }

  unarchive() {
    this.entityDashboardService.setArchiveFlag(this._data, false);
  }

  accept() {
    this.addReview(enumReviewStatus.Accepted);
  }

  reject() {
    this.addReview(enumReviewStatus.Rejected);
  }

  review() {
    this.addReview(enumReviewStatus.Reviewed);
  }

  addReview(action: enumReviewStatus) {
    this.dialog
      .open(ReviewDialogComponent, {
        data: {
          action,
          subject: this._data.entity,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result && result.data) {
          if (result.data.action === enumReviewStatus.Accepted) {
            this.eventService.acceptSubject(result.data.subject);
          }
          if (result.data.action === enumReviewStatus.Rejected) {
            this.eventService.rejectSubject(result.data.subject);
          }
          if (result.data.action === enumReviewStatus.Reviewed) {
            this.eventService.reviewSubject(result.data.subject);
          }
        }
      });
  }

  addAssessment() {
    this.openAssessment();
  }

  editAssessment(assessment) {
    this.openAssessment(assessment);
  }

  openAssessment(assessment?) {
    this.dialog
      .open(AssessmentDialogComponent, {
        data: {
          assessment,
          subject: this._data.entity,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result && result.data) {
          if (result.data.action === enumReviewStatus.Accepted) {
            this.eventService.acceptSubject(result.data.subject);
          }
          if (result.data.action === enumReviewStatus.Rejected) {
            this.eventService.rejectSubject(result.data.subject);
          }
        }
      });
  }

  private entityChanged(entity, message?: string) {
    // if the dashboard is currently unlocked, lock it and save changes
    if (this.hasChanges || (entity.archive && !this.layoutBuilder.locked)) {
      this.saveDashboard();
    }

    // when the subject has been saved, fire notification to update the task stats
    this._data.service.onAfterSave.pipe(take(1)).subscribe((_) => {
      this.eventService.updateTaskStats();
    });

    this._data.service.save(entity, message, true, false);

    // save changes to archived flag
    this.readOnly = entity.archive;

    this.updateSecurity();
  }

  updateSecurity(user: User = undefined) {
    // Hide Security options if user has Org and Limit Access
    user = user || this.userManager.getCurrentUser();
    if (!user) {
      this.showSecurity = false;
      return;
    }

    this.showSecurity = !(
      user.organisationId > 0 && environment.limitAccessToSubjectsByOrg === true
    );
  }

  trackById(index, item) {
    return item.id;
  }

  private checkAndPromptReviewStatus(data: any) {
    //need to check here on the entity.Status(?) and the flag
    if (
      data.entity &&
      data.entity.subjectType &&
      this.isPrimarySubject &&
      this.canReview
    ) {
      // get max number of days specified between reviews for this subject type
      this.subjectSettingService
        .getBySubjectTypeId(data.entity.subjectType.id)
        .pipe(take(1))
        .subscribe((subjectSetting: SubjectSetting) => {
          if (
            subjectSetting &&
            subjectSetting.maxDaysBetweenReviews &&
            subjectSetting.maxDaysBetweenReviews > 0
          ) {
            const days = subjectSetting.maxDaysBetweenReviews;
            if (
              new Date(data.entity.dateCreated) <
                new Date(new Date().setDate(new Date().getDate() - days)) &&
              (!data.entity.reviewDate ||
                new Date(data.entity.reviewDate) <
                  new Date(new Date().setDate(new Date().getDate() - days)))
            ) {
              let lastReviewMsg;
              //
              if (data.entity.reviewDate) {
                const lastReviewDate = new DateFormatPipe('en-GB').transform(
                  data.entity.reviewDate
                );
                lastReviewMsg = `was last reviewed on ${lastReviewDate}`;
              } else {
                lastReviewMsg = 'has not been reviewed';
              }
              const info = new MessageBoxParameters(
                `This ${this.subjectTypeName} ${lastReviewMsg}.All ${plural(
                  this.subjectTypeName
                )} must be reviewed every ${days} days.`,
                `${this.subjectTypeName} requires review`,
                MessageBoxType.Ok
              );
              this.dialog.open(MessageBoxComponent, { data: info });
            }
          }
        });
    }
  }
}
